import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout.jsx';
import { TextField } from '../components/Field.jsx';
import { ErrorAlert, InfoAlert, Loader } from '../components/States.jsx';
import useForm from '../hooks/useForm.js';
import { useAuth } from '../store/useAuth.js';
import { useGetSetupQuery } from '../store/apiSlice.js';
import { registerSchema, MIN_PASSWORD } from '../validation/schemas.js';

const BLANK = { name: '', email: '', phone1: '', password: '', confirmPassword: '' };

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const { data: setup, isLoading: loading } = useGetSetupQuery();

  const form = useForm(BLANK, registerSchema);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const [submittedPending, setSubmittedPending] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setFailure('');
    if (!form.validate()) return;

    setBusy(true);
    try {
      const { confirmPassword, ...payload } = form.values;
      const { user: created, meta } = await register(payload);

      // The first account is the administrator and is signed in straight away.
      if (created) navigate('/', { replace: true });
      else if (meta?.pending) setSubmittedPending(true);
    } catch (err) {
      form.applyServerErrors(err.details);
      setFailure(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Create account">
        <Loader label="Checking…" />
      </AuthLayout>
    );
  }

  if (submittedPending) {
    return (
      <AuthLayout
        title="Account created"
        subtitle="One more step before you can sign in."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <InfoAlert>
          An administrator has to approve your account. You will be able to sign in as soon as they do.
        </InfoAlert>
      </AuthLayout>
    );
  }

  if (!setup?.needsFirstAdmin && !setup?.selfRegistrationOpen) {
    return (
      <AuthLayout title="Registration is closed" footer={<Link to="/login">Back to sign in</Link>}>
        <InfoAlert>Ask an administrator to create an account for you.</InfoAlert>
      </AuthLayout>
    );
  }

  const firstAdmin = setup?.needsFirstAdmin;

  return (
    <AuthLayout
      title={firstAdmin ? 'Create the administrator' : 'Create account'}
      subtitle={
        firstAdmin
          ? 'This database has no users yet, so this first account becomes the administrator.'
          : 'An administrator will approve your account before you can sign in.'
      }
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <ErrorAlert message={failure} />

      <form onSubmit={submit} noValidate>
        <TextField label="Name" placeholder="Full name" autoComplete="name" maxLength={60} full autoFocus {...form.fieldProps('name')} />
        <TextField type="email" label="Email ID" placeholder="name@email.com" autoComplete="username" maxLength={120} full {...form.fieldProps('email')} />
        <TextField label="Phone Number" placeholder="+91 98290 00000" autoComplete="tel" full {...form.fieldProps('phone1')} />
        <TextField
          type="password"
          label="Password"
          placeholder={`At least ${MIN_PASSWORD} characters`}
          autoComplete="new-password"
          hint={`${MIN_PASSWORD}+ characters, with at least one letter and one number.`}
          full
          {...form.fieldProps('password')}
        />
        <TextField
          type="password"
          label="Confirm Password"
          placeholder="Type it again"
          autoComplete="new-password"
          full
          {...form.fieldProps('confirmPassword')}
        />
        <button className="btn primary auth-submit" type="submit" disabled={busy}>
          {busy ? 'Creating…' : firstAdmin ? 'Create administrator' : 'Create account'}
        </button>
      </form>
    </AuthLayout>
  );
}
